import {
    useEffect,
    useMemo,
    useRef,
    useState,
} from 'react';
import './OurVideo.scss';
import { useTranslation } from "react-i18next";
import {WorkItem} from "../../../types";

interface VideoCardProps {
    item: WorkItem;
    isVisible: boolean;
    onMouseEnter: () => void;
    onMouseLeave: () => void;
    onClick: (e: React.MouseEvent<HTMLAnchorElement>) => void;
}

const LOOP_MULTIPLIER = 3;
const AUTOSCROLL_SPEED = 48;

const isDirectVideo = (url: string) =>
    /\.(mp4|mov|webm|ogg|avi)$/i.test(url);

export function VideoCard({
                              item,
                              isVisible,
                              onMouseEnter,
                              onMouseLeave,
                              onClick,
                          }: VideoCardProps) {
    const cardRef = useRef<HTMLAnchorElement>(null);
    const videoRef = useRef<HTMLVideoElement>(null);

    const [cardInView, setCardInView] = useState(false);
    const [videoLoaded, setVideoLoaded] = useState(false);
    const [videoError, setVideoError] = useState(false);

    const { t } = useTranslation();

    const shouldPlay = isVisible && cardInView;
    const canUseVideo =
        isDirectVideo(item.video) && !videoError;

    /**
     * Rileva quando la card è visibile.
     */
    useEffect(() => {
        const el = cardRef.current;

        if (!el) return;

        const observer = new IntersectionObserver(
            ([entry]) => {
                setCardInView(entry.isIntersecting);
            },
            {
                threshold: 0.25,
            }
        );

        observer.observe(el);

        return () => observer.disconnect();
    }, []);

    /**
     * Gestione autoplay video.
     */
    useEffect(() => {
        const video = videoRef.current;

        if (!video || !canUseVideo) return;

        if (!shouldPlay) {
            video.pause();
            return;
        }

        video.muted = true;

        video.play().catch(() => {
            // Autoplay bloccato dal browser.
        });

        return () => {
            video.pause();
        };
    }, [shouldPlay, canUseVideo]);

    const handleVideoError = () => {
        setVideoError(true);
        setVideoLoaded(false);
    };

    const showThumbnail =
        !videoLoaded || !canUseVideo;

    return (
        <a
            ref={cardRef}
            href={item.href}
            target="_blank"
            rel="noopener noreferrer"
            draggable={false}
            className="work-card"
            onMouseEnter={onMouseEnter}
            onMouseLeave={onMouseLeave}
            onClick={onClick}
            onDragStart={(e) => e.preventDefault()}
        >
            <div className="work-card__media">
                {showThumbnail && (
                    <img
                        src={item.thumbnail}
                        alt={item.title}
                        loading="lazy"
                        draggable={false}
                        className="work-card__thumbnail"
                    />
                )}

                {canUseVideo && (
                    <video
                        ref={videoRef}
                        src={item.video}
                        loop
                        muted
                        playsInline
                        preload="none"
                        className={`work-card__video ${
                            videoLoaded
                                ? 'is-loaded'
                                : ''
                        }`}
                        onLoadedData={() =>
                            setVideoLoaded(true)
                        }
                        onError={handleVideoError}
                    />
                )}

                <div className="work-card__overlay" />
            </div>


            <div className="work-card__info">
                <p className="work-card__views">
                    {item.views}
                </p>

                {String(item.server) != "unknown" && (
                    <p className="work-card__server">
                        {t("works.videos.for")} {item.server || item.title}
                    </p>
                )}
            </div>
        </a>
    );
}


type OurVideoProps = {
    allWorkItems: WorkItem[];
    title: boolean
};


export default function OurVideo({allWorkItems, title}: OurVideoProps) {
    const sectionRef = useRef<HTMLElement>(null);
    const trackRef = useRef<HTMLDivElement>(null);

    const { t } = useTranslation();

    const workItems = useMemo(
        () =>
            allWorkItems.filter(
                (item) => item.podium === true
            ),
        [allWorkItems]
    );

    const [inView, setInView] = useState(false);
    const [isDragging, setIsDragging] = useState(false);

    const carouselRef = useRef({
        pointerDown: false,
        isDragging: false,
        startX: 0,
        startScroll: 0,
        moved: false,
        pointerId: null as number | null,
    });

    const isHoveringCardRef = useRef(false);

    const inViewRef = useRef(false);
    const animationRef = useRef<number | null>(null);


    /**
     * Il browser blocca scrollLeft a
     * scrollWidth - clientWidth.
     *
     * La finestra di wrap deve quindi
     * stare dentro quel limite, altrimenti
     * lo scroll resta bloccato e il
     * carosello non scorrerà mai.
     */
    const getLoopBounds = (
        track: HTMLDivElement
    ) => {
        const setWidth =
            track.scrollWidth /
            LOOP_MULTIPLIER;

        const maxScroll =
            track.scrollWidth -
            track.clientWidth;

        const start = Math.max(
            0,
            Math.min(
                setWidth,
                maxScroll - setWidth
            )
        );

        return { setWidth, start };
    };

    const normalizeScroll = (
        scrollLeft: number,
        setWidth: number,
        start: number
    ) => {
        if (setWidth <= 0) {
            return scrollLeft;
        }

        while (scrollLeft < start) {
            scrollLeft += setWidth;
        }

        while (scrollLeft >= start + setWidth) {
            scrollLeft -= setWidth;
        }

        return scrollLeft;
    };

    useEffect(() => {
        const section = sectionRef.current;

        if (!section) return;

        const observer =
            new IntersectionObserver(
                ([entry]) => {
                    const visible =
                        entry.isIntersecting;

                    inViewRef.current = visible;
                    setInView(visible);
                },
                {
                    threshold: 0.1,
                }
            );

        observer.observe(section);

        return () => observer.disconnect();
    }, []);



    const duplicatedItems = useMemo(() => {
        if (workItems.length === 0) {
            return [];
        }

        return Array.from(
            {
                length: LOOP_MULTIPLIER,
            },
            (_, loopIndex) =>
                workItems.map(
                    (item, itemIndex) => ({
                        ...item,
                        key: `${loopIndex}-${itemIndex}-${item.title}`,
                    })
                )
        ).flat();
    }, [workItems]);


    useEffect(() => {
        const track = trackRef.current;

        if (
            !track ||
            workItems.length === 0
        ) {
            return;
        }

        requestAnimationFrame(() => {
            const {
                setWidth,
                start,
            } = getLoopBounds(
                track
            );

            track.scrollLeft = start;
        });
    }, [workItems]);

    useEffect(() => {
        const track = trackRef.current;

        if (!track) return;

        let previousTime =
            performance.now();

        const animate = (
            currentTime: number
        ) => {
            const delta =
                Math.min(
                    currentTime -
                    previousTime,
                    32
                ) / 1000;

            previousTime = currentTime;

            if (
                inViewRef.current &&
                !carouselRef.current
                    .isDragging &&
                !isHoveringCardRef.current &&
                workItems.length > 0
            ) {
                const {
                    setWidth,
                    start,
                } = getLoopBounds(
                    track
                );

                if (setWidth > 0) {
                    const nextScroll =
                        track.scrollLeft +
                        AUTOSCROLL_SPEED *
                        delta;

                    track.scrollLeft =
                        normalizeScroll(
                            nextScroll,
                            setWidth,
                            start
                        );
                }
            }

            animationRef.current =
                requestAnimationFrame(
                    animate
                );
        };

        animationRef.current =
            requestAnimationFrame(
                animate
            );

        return () => {
            if (
                animationRef.current !==
                null
            ) {
                cancelAnimationFrame(
                    animationRef.current
                );
            }
        };
    }, [workItems]);

    const handleCardMouseEnter =
        () => {
            isHoveringCardRef.current =
                true;
        };

    const handleCardMouseLeave =
        () => {
            isHoveringCardRef.current =
                false;
        };



    const handleCardClick = (
        e: React.MouseEvent<HTMLAnchorElement>
    ) => {
        if (carouselRef.current.moved) {
            e.preventDefault();
            e.stopPropagation();

            carouselRef.current.moved =
                false;
        }
    };

    const handlePointerDown = (
        e: React.PointerEvent<HTMLDivElement>
    ) => {
        const track = trackRef.current;

        if (!track) return;

        if (
            e.pointerType === 'mouse' &&
            e.button !== 0
        ) {
            return;
        }

        carouselRef.current = {
            pointerDown: true,
            isDragging: false,
            startX: e.clientX,
            startScroll:
            track.scrollLeft,
            moved: false,
            pointerId: e.pointerId,
        };
    };

    const handlePointerMove = (
        e: React.PointerEvent<HTMLDivElement>
    ) => {
        const track = trackRef.current;

        if (!track) return;

        const state =
            carouselRef.current;

        if (!state.pointerDown) {
            return;
        }

        const delta =
            e.clientX - state.startX;

        /**
         * Finché siamo sotto 5px,
         * consideriamo l'azione un click.
         */
        if (!state.isDragging) {
            if (Math.abs(delta) < 5) {
                return;
            }

            /**
             * Da qui in poi è un vero drag.
             */
            state.isDragging = true;
            state.moved = true;

            /**
             * SOLO ADESSO facciamo pointer capture.
             *
             * Questo non interferisce con
             * i click normali.
             */
            if (
                state.pointerId !== null &&
                !track.hasPointerCapture(
                    state.pointerId
                )
            ) {
                track.setPointerCapture(
                    state.pointerId
                );
            }

            setIsDragging(true);
        }

        const {
            setWidth,
            start,
        } = getLoopBounds(track);

        if (setWidth <= 0) {
            return;
        }

        const nextScroll =
            state.startScroll - delta;

        track.scrollLeft =
            normalizeScroll(
                nextScroll,
                setWidth,
                start
            );
    };

    const handlePointerUp = (
        e: React.PointerEvent<HTMLDivElement>
    ) => {
        const track = trackRef.current;
        const state =
            carouselRef.current;

        if (
            track &&
            state.pointerId !== null &&
            track.hasPointerCapture(
                state.pointerId
            )
        ) {
            track.releasePointerCapture(
                state.pointerId
            );
        }

        state.pointerDown = false;
        state.isDragging = false;
        state.pointerId = null;

        setIsDragging(false);

        /**
         * NON resettiamo moved.
         *
         * Se era un drag, il click successivo
         * verrà intercettato da handleCardClick().
         *
         * Se era un click normale,
         * moved è già false.
         */
    };

    const handlePointerCancel = (
        e: React.PointerEvent<HTMLDivElement>
    ) => {
        const track = trackRef.current;
        const state =
            carouselRef.current;

        if (
            track &&
            state.pointerId !== null &&
            track.hasPointerCapture(
                state.pointerId
            )
        ) {
            track.releasePointerCapture(
                state.pointerId
            );
        }

        carouselRef.current = {
            pointerDown: false,
            isDragging: false,
            startX: 0,
            startScroll: 0,
            moved: false,
            pointerId: null,
        };

        setIsDragging(false);
    };


    return (
        <section
            id="work"
            ref={sectionRef}
            className="work-section"
        >
            <div className="work-section__container">
                {title && (
                    <div className="work-section__heading">
                        <h2 className="work-section__title">
                            {t("works.videos.title")}
                        </h2>

                        <p className="work-section__subtitle">
                            {t("works.videos.subtitle")}
                        </p>
                    </div>
                )}

                <div className="work-carousel">
                    <div className="work-carousel__fade work-carousel__fade--left" />

                    <div className="work-carousel__fade work-carousel__fade--right" />

                    <div
                        ref={trackRef}
                        className={`work-track ${
                            isDragging
                                ? 'is-dragging'
                                : ''
                        }`}
                        onPointerDown={
                            handlePointerDown
                        }
                        onPointerMove={
                            handlePointerMove
                        }
                        onPointerUp={
                            handlePointerUp
                        }
                        onPointerCancel={
                            handlePointerCancel
                        }
                    >
                        {duplicatedItems.map(
                            (item) => (
                                <VideoCard
                                    key={item.key}
                                    item={item}
                                    isVisible={
                                        inView
                                    }
                                    onMouseEnter={
                                        handleCardMouseEnter
                                    }
                                    onMouseLeave={
                                        handleCardMouseLeave
                                    }
                                    onClick={
                                        handleCardClick
                                    }
                                />
                            )
                        )}
                    </div>
                </div>
            </div>
        </section>
    );
}
